import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SyncIcon from '@mui/icons-material/Sync';
import CommandRenderer from './CommandRenderer';

const ProcessTreeItem = ({ process, depth = 0 }) => {
  const { icon, color } = useMemo(() => {
    switch (process.status) {
      case 'processing':
        return { icon: <CircularProgress size={16} thickness={5} />, color: 'primary.main' };
      case 'complete':
        return { icon: <CheckCircleIcon fontSize="small" color="success" />, color: 'success.main' };
      case 'error':
        return { icon: <ErrorIcon fontSize="small" color="error" />, color: 'error.main' };
      default:
        return { icon: <SyncIcon fontSize="small" color="action" />, color: 'text.secondary' };
    }
  }, [process.status]);

  const displayEvents = process.events.filter(e => 
    e.msg_type !== 'AGENT_INIT' && 
    e.msg_type !== 'AGENT_COMPLETE' && 
    e.type !== 'resource_sync'
  );

  return (
    <Box sx={{ mb: 2, position: 'relative' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, zIndex: 1, position: 'relative' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', mr: 1, zIndex: 2 }}>
          {icon}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ color }}>
            {process.title}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ pl: 4, ml: '13px', borderLeft: '1px solid', borderColor: 'divider', position: 'relative', zIndex: 1 }}>
        {displayEvents.map((event, commandIndex) => (
          <Box key={`command-${commandIndex}`} sx={{ mb: 1 }}>
            <CommandRenderer command={event} />
          </Box>
        ))}
        {process.children.map(child => (
          <ProcessTreeItem key={child.id} process={child} depth={depth + 1} />
        ))}
      </Box>
    </Box>
  );
};

ProcessTreeItem.propTypes = {
  process: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    status: PropTypes.string,
    events: PropTypes.array,
    children: PropTypes.array,
  }).isRequired,
  depth: PropTypes.number,
};

export default ProcessTreeItem;